import {
  CustomOptions,
  CustomOptionsStatus,
  CustomPromptCheckInputMode,
  CustomPromptCheckSettings,
  ExternalCheckSettingsInput,
  ExternalMaskingDetectorSettingsInput,
  ExternalMaskingErrorPolicy,
  ExternalReplacementMode,
  GuardrailBehavior,
  GuardrailErrorPolicy,
  GuardrailSettingsState,
  MaskingDemaskingMode,
  MaskingMode,
  MaskingSettingsInput,
  MaskingTokenMetaMode,
  RegexCheckSettings,
  RegexMaskingDetectorSettings,
} from '../../../../../gamma-api';

import { CheckTypeValue, MaskingDetectorTypeValue } from './types';

export enum MaskingEntityType {
  Person = 'PERSON',
  EmailAddress = 'EMAIL_ADDRESS',
  PhoneNumber = 'PHONE_NUMBER',
  CreditCard = 'CREDIT_CARD',
  Address = 'ADDRESS',
  RuInn = 'RU_INN',
  RuSnils = 'RU_SNILS',
  RuPassport = 'RU_PASSPORT',
  RuZipcode = 'RU_ZIPCODE',
}

export const DEFAULT_MASKING_ENTITY_TYPES: MaskingEntityType[] = [
  MaskingEntityType.Person,
  MaskingEntityType.EmailAddress,
  MaskingEntityType.PhoneNumber,
  MaskingEntityType.CreditCard,
  MaskingEntityType.Address,
  MaskingEntityType.RuInn,
  MaskingEntityType.RuSnils,
  MaskingEntityType.RuPassport,
  MaskingEntityType.RuZipcode,
];

export const DEFAULT_CUSTOM_PROMPT_CHECK_CONFIDENCE_THRESHOLD = 0.7;
export const DEFAULT_CUSTOM_PROMPT_CHECK_MAX_TURNS = 10;
export const DEFAULT_BLOCK_MESSAGE = 'Blocked by security policy';

export const DEFAULT_CUSTOM_OPTIONS: CustomOptions = {
  status: CustomOptionsStatus.Disabled,
  json_value: '',
};

export const DEFAULT_MASKING_SETTINGS: MaskingSettingsInput = {
  state: GuardrailSettingsState.Disabled,
  mode: MaskingMode.ReplaceWithToken,
  demasking_mode: MaskingDemaskingMode.WithDemasking,
  token_meta: MaskingTokenMetaMode.Enabled,
  custom_prompt: '',
  detect_all_supported_entity_types: false,
  builtin_entities: DEFAULT_MASKING_ENTITY_TYPES,
  detectors: [],
};

export const DEFAULT_REGEX_DETECTOR_SETTINGS: RegexMaskingDetectorSettings & {
  type: typeof MaskingDetectorTypeValue.Regex;
} = {
  state: GuardrailSettingsState.Enabled,
  type: MaskingDetectorTypeValue.Regex,
  name: '',
  order: 0,
  entity_type: 'UNKNOWN',
  patterns: [],
  flags: [],
};

export const DEFAULT_EXTERNAL_DETECTOR_SETTINGS: ExternalMaskingDetectorSettingsInput & {
  type: typeof MaskingDetectorTypeValue.External;
} = {
  state: GuardrailSettingsState.Enabled,
  type: MaskingDetectorTypeValue.External,
  name: '',
  order: 0,
  api_url: '',
  timeout_seconds: 10,
  max_retries: 1,
  request_headers: {},
  custom_options: DEFAULT_CUSTOM_OPTIONS,
  on_error: ExternalMaskingErrorPolicy.FailClosed,
  replacement_mode: ExternalReplacementMode.PipelineGenerated,
};

export const DEFAULT_CUSTOM_PROMPT_CHECK_SETTINGS: CustomPromptCheckSettings & {
  type: 'CustomPromptCheck';
  name?: string;
} = {
  state: GuardrailSettingsState.Enabled,
  type: CheckTypeValue.CustomPromptCheck,
  name: '',
  order: 0,
  behavior: GuardrailBehavior.Block,
  block_message: DEFAULT_BLOCK_MESSAGE,
  on_error: GuardrailErrorPolicy.FailClosed,
  timeout_seconds: 60,
  llm: {},
  use_final_answer_tool: false,
  prompt: '',
  confidence_threshold: DEFAULT_CUSTOM_PROMPT_CHECK_CONFIDENCE_THRESHOLD,
  max_turns: DEFAULT_CUSTOM_PROMPT_CHECK_MAX_TURNS,
  input_check_mode: CustomPromptCheckInputMode.UserMessageOnly,
  include_reasoning: false,
};

export const DEFAULT_EXTERNAL_CHECK_SETTINGS: ExternalCheckSettingsInput & {
  type: 'ExternalCheck';
  name?: string;
} = {
  state: GuardrailSettingsState.Enabled,
  type: CheckTypeValue.ExternalCheck,
  name: '',
  order: 0,
  behavior: GuardrailBehavior.Block,
  block_message: DEFAULT_BLOCK_MESSAGE,
  on_error: GuardrailErrorPolicy.FailClosed,
  timeout_seconds: 60,
  api_url: '',
  max_retries: 1,
  request_headers: {},
  custom_options: DEFAULT_CUSTOM_OPTIONS,
  max_turns: DEFAULT_CUSTOM_PROMPT_CHECK_MAX_TURNS,
};

export const DEFAULT_REGEX_CHECK_SETTINGS: RegexCheckSettings & {
  type: 'RegexCheck';
  name?: string;
} = {
  state: GuardrailSettingsState.Enabled,
  type: CheckTypeValue.RegexCheck,
  name: '',
  order: 0,
  behavior: GuardrailBehavior.Block,
  block_message: DEFAULT_BLOCK_MESSAGE,
  on_error: GuardrailErrorPolicy.FailClosed,
  timeout_seconds: 10,
  patterns: [],
  flags: [],
};
